import dot from 'dot-prop-immutable';
import { createSelector } from 'reselect';

import type {
  PreparedFeature,
  PreparedFeedbackItem,
} from '@frontend/partviewer';

import { FEATURE_LOADING_TYPES } from 'modules/common/viewer/cadViewer/constants';

import type { RootState } from 'store/configureStore';
import type { ActionWithPayload } from 'store/types';

import {
  types as cadViewerTypes,
  type CadViewerInterrogationInfoPayload,
} from '../actions';
import { types, type GeometricAnalysisDataAction } from './actions';
import type {
  CachedFeatureData,
  FeatureLoadingType,
  PartFileGeometricAnalysisDataLoadingState,
} from './types';

type FeatureLoadingById = {
  [FEATURE_LOADING_TYPES.FEATURE]: Record<number, boolean>;
  [FEATURE_LOADING_TYPES.FEEDBACK]: Record<number, boolean>;
};

type GeometricAnalysisModuleState = {
  cachedFeatureData: CachedFeatureData;
  featureLoadingById: FeatureLoadingById;
  geometricAnalysisQueue: string[];
  partFileGeometricAnalysisData: Record<
    string,
    PartFileGeometricAnalysisDataLoadingState
  >;
};

const partFileGeometricAnalysisDataInitialState: PartFileGeometricAnalysisDataLoadingState =
  {
    geometricAnalysisData: {
      features: [],
      feedbackItems: [],
      interrogationFiles: [],
      setups: [],
    },
    hasLoaded: false,
    isLoading: false,
  } as const;

const initialState: GeometricAnalysisModuleState = {
  cachedFeatureData: {
    [FEATURE_LOADING_TYPES.FEATURE]: {},
    [FEATURE_LOADING_TYPES.FEEDBACK]: {},
  },
  featureLoadingById: {
    [FEATURE_LOADING_TYPES.FEATURE]: {},
    [FEATURE_LOADING_TYPES.FEEDBACK]: {},
  },
  geometricAnalysisQueue: [], // queue of partfile uuids that need to be fetched
  partFileGeometricAnalysisData: {}, // hash map of partFileUuids to their artifacts from geometric analysis
} as const;

type CombinedAction =
  | GeometricAnalysisDataAction
  | ActionWithPayload<typeof cadViewerTypes.CAD_VIEWER_RESET>;

const reducer = (
  state: GeometricAnalysisModuleState = initialState,
  action: CombinedAction,
): GeometricAnalysisModuleState => {
  switch (action.type) {
    case cadViewerTypes.CAD_VIEWER_RESET: {
      // NOTE: limiting amount of cached feature data to prevent bloating memory
      // if total amount of cached entries is greater than 20k, we will blow it away
      const featureCount = Object.keys(
        state.cachedFeatureData[FEATURE_LOADING_TYPES.FEATURE],
      ).length;
      const feedbackCount = Object.keys(
        state.cachedFeatureData[FEATURE_LOADING_TYPES.FEEDBACK],
      ).length;
      return {
        ...initialState,
        cachedFeatureData:
          featureCount + feedbackCount > 20000
            ? initialState.cachedFeatureData
            : state.cachedFeatureData,
      };
    }
    case types.FEATURE_LOADING_REQUEST: {
      const { features, type } = action.payload;
      const nextLoadingById: Record<number, boolean> = {};
      for (const feature of features) {
        nextLoadingById[feature.id] = true;
      }
      return dot.merge(state, `featureLoadingById.${type}`, nextLoadingById);
    }
    case types.FEATURE_LOADING_SUCCESS: {
      const { featureContentsById, type } = action.payload;
      const nextLoadingById: Record<number, boolean> = {};
      for (const featureId of Object.keys(featureContentsById)) {
        nextLoadingById[Number(featureId)] = false;
      }
      return {
        ...state,
        cachedFeatureData: dot.merge(
          state.cachedFeatureData,
          type,
          featureContentsById,
        ),
        featureLoadingById: dot.merge(
          state.featureLoadingById,
          type,
          nextLoadingById,
        ),
      };
    }
    case types.FEATURE_LOADING_ERROR: {
      const { featureIds, type } = action.payload;
      const nextLoadingById: Record<number, boolean> = {};
      for (const featureId of featureIds) {
        nextLoadingById[featureId] = false;
      }
      return dot.merge(state, `featureLoadingById.${type}`, nextLoadingById);
    }
    case types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_ERROR: {
      const { partFileUuid } = action.payload;
      return {
        ...state,
        partFileGeometricAnalysisData: {
          ...state.partFileGeometricAnalysisData,
          [partFileUuid]: {
            ...state.partFileGeometricAnalysisData[partFileUuid],
            isLoading: false,
          },
        },
      };
    }
    case types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_INIT: {
      const { partFileUuid } = action.payload;
      return {
        ...state,
        partFileGeometricAnalysisData: {
          ...state.partFileGeometricAnalysisData,
          [partFileUuid]: {
            ...(state.partFileGeometricAnalysisData?.partFileUuid ||
              partFileGeometricAnalysisDataInitialState),
            isLoading: true,
          },
        },
      };
    }
    case types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_SUCCESS: {
      const { partFileUuid, geometricAnalysisData } = action.payload;
      return {
        ...state,
        partFileGeometricAnalysisData: {
          ...state.partFileGeometricAnalysisData,
          [partFileUuid]: {
            ...state.partFileGeometricAnalysisData[partFileUuid],
            geometricAnalysisData,
            hasLoaded: true,
            isLoading: false,
          },
        },
      };
    }
    case types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_END: {
      const { partFileUuid: partFileUuidToAdd } = action.payload;
      return state.geometricAnalysisQueue.includes(partFileUuidToAdd)
        ? state
        : {
            ...state,
            geometricAnalysisQueue: [
              ...state.geometricAnalysisQueue,
              partFileUuidToAdd,
            ],
          };
    }
    case types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_START: {
      const { partFileUuid: partFileUuidToAdd } = action.payload;
      return state.geometricAnalysisQueue.includes(partFileUuidToAdd)
        ? state
        : {
            ...state,
            geometricAnalysisQueue: [
              partFileUuidToAdd,
              ...state.geometricAnalysisQueue,
            ],
          };
    }
    case types.GEOMETRIC_ANALYSIS_QUEUE_REMOVE: {
      const { partFileUuid: partFileUuidToRemove } = action.payload;
      return {
        ...state,
        geometricAnalysisQueue: state.geometricAnalysisQueue.filter(
          (partFileUuid) => partFileUuid !== partFileUuidToRemove,
        ),
      };
    }
    case types.PART_FILE_GEOMETRIC_ANALYSIS_RECEIVE: {
      const { partFileUuid, geometricAnalysisData } = action.payload;
      return dot.set(
        state,
        `partFileGeometricAnalysisData.${partFileUuid}.geometricAnalysisData`,
        geometricAnalysisData,
      );
    }
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): GeometricAnalysisModuleState =>
  state.modules.common.viewer.cadViewer.geometricAnalysis;

export const getCachedFeatureData = (state: RootState) =>
  getScope(state).cachedFeatureData;

const getFeatureLoadingById = (state: RootState) =>
  getScope(state).featureLoadingById;

export const getIsAnyFeatureLoadingByType = (
  state: RootState,
  type: FeatureLoadingType,
) => {
  const featureLoadingById = getFeatureLoadingById(state);
  return Object.values(featureLoadingById[type]).length
    ? Object.values(featureLoadingById[type]).find((v) => v)
    : false;
};

export const getGeometricAnalysisQueue = (state: RootState) =>
  getScope(state).geometricAnalysisQueue;

export const getPartFileGeometricAnalysisData = (state: RootState) =>
  getScope(state).partFileGeometricAnalysisData;

export const getIsAnyGeometricAnalysisDataFetching = (state: RootState) => {
  const geometricAnalysisData = getPartFileGeometricAnalysisData(state);
  return Object.keys(geometricAnalysisData).some(
    (partFileUuid) => geometricAnalysisData[partFileUuid]?.isLoading,
  );
};

export const getGeometricAnalysisDataForPartFile = (
  state: RootState,
  partFileUuid: string,
) =>
  getPartFileGeometricAnalysisData(state)?.[partFileUuid]
    ?.geometricAnalysisData ?? null;

export const getHasPartFileGeometricAnalysisLoaded = (
  state: RootState,
  partFileUuid: string,
) =>
  getPartFileGeometricAnalysisData(state)?.[partFileUuid]?.hasLoaded ?? false;

export const getAllFeaturesForPartFile = (
  state: RootState,
  partFileUuid: string,
) =>
  getPartFileGeometricAnalysisData(state)?.[partFileUuid]?.geometricAnalysisData
    ?.features ?? [];

export const getAllFeedbackItemsForPartFile = (
  state: RootState,
  partFileUuid: string,
) =>
  getPartFileGeometricAnalysisData(state)?.[partFileUuid]?.geometricAnalysisData
    ?.feedbackItems ?? [];

export const getFeatures = createSelector(
  [
    getPartFileGeometricAnalysisData,
    (_: RootState, partFileUuid: string) => partFileUuid,
    (
      _: RootState,
      __: string,
      interrogationInfo: CadViewerInterrogationInfoPayload,
    ) => interrogationInfo,
  ],
  (
    geometricAnalysisData: Record<
      string,
      PartFileGeometricAnalysisDataLoadingState
    >,
    partFileUuid: string,
    interrogationInfo: CadViewerInterrogationInfoPayload,
  ) => {
    /*
    Returns the features for a given partfileUuid and interrogation.
    */
    const partFileGeometricAnalysisData =
      geometricAnalysisData?.[partFileUuid]?.geometricAnalysisData ?? {};
    const { interrogationFiles, features } = partFileGeometricAnalysisData;

    if (!features) {
      return [];
    }
    // innate features are features not specific to any interrogation
    const innateFeatures = features.filter(({ isInnate }) => isInnate);

    const partFileInterrogationFile = (interrogationFiles || []).find(
      ({ id }) => id === interrogationInfo?.interrogationFileId,
    );
    if (!interrogationInfo || !partFileInterrogationFile) {
      return innateFeatures;
    }

    // find interrogation specific features
    const { featureIds: interrogationFeatureIds } = partFileInterrogationFile;
    const interrogationFeatures = features.filter(({ id }) =>
      interrogationFeatureIds.includes(id),
    );

    return innateFeatures.concat(interrogationFeatures);
  },
);

export const getFeedbackItems = createSelector(
  [
    getPartFileGeometricAnalysisData,
    (_: RootState, partFileUuid: string) => partFileUuid,
    (
      _: RootState,
      __: string,
      interrogationInfo: CadViewerInterrogationInfoPayload,
    ) => interrogationInfo,
  ],
  (geometricAnalysisData, partFileUuid, interrogationInfo) => {
    /*
    Returns the feedbackItems for a given partfileUuid and interrogation.
    */
    const partFileGeometricAnalysisData =
      geometricAnalysisData?.[partFileUuid]?.geometricAnalysisData ?? {};
    const { interrogationFiles, feedbackItems } = partFileGeometricAnalysisData;

    if (!feedbackItems) {
      return [];
    }
    // innate feedbackItems not specific to any interrogation
    const innateFeedbackItems = feedbackItems.filter(
      ({ isInnate }) => isInnate,
    );

    const partFileInterrogationFile = (interrogationFiles || []).find(
      ({ id }) => id === interrogationInfo?.interrogationFileId,
    );
    if (!interrogationInfo || !partFileInterrogationFile) {
      return innateFeedbackItems;
    }

    // find interrogation specific features
    const { feedbackIds: interrogationFeedbackItemIds } =
      partFileInterrogationFile;
    const interrogationFeedbackItems = feedbackItems.filter(({ id }) =>
      interrogationFeedbackItemIds.includes(id),
    );

    return innateFeedbackItems.concat(interrogationFeedbackItems);
  },
);

export const getSetups = createSelector(
  [
    getPartFileGeometricAnalysisData,
    (_: RootState, partFileUuid: string) => partFileUuid,
    (
      _: RootState,
      __: string,
      interrogationInfo: CadViewerInterrogationInfoPayload,
    ) => interrogationInfo,
  ],
  (
    geometricAnalysisData,
    partFileUuid: string,
    interrogationInfo: CadViewerInterrogationInfoPayload,
  ) => {
    /*
    Returns the setups for a given partfileUuid and interrogation.
    */
    const partFileGeometricAnalysisData =
      geometricAnalysisData?.[partFileUuid]?.geometricAnalysisData ?? {};
    const { setups } = partFileGeometricAnalysisData;

    if (!setups || !interrogationInfo) {
      return [];
    }

    return setups.filter(
      (setup) =>
        setup.interrogationFileId == interrogationInfo?.interrogationFileId,
    );
  },
);

type MergedGeometricAnalysisData = {
  features: PreparedFeature[];
  feedbackItems: PreparedFeedbackItem[];
};

/*
Merges all of the individual part files analysis data into a single instance.
This was done to follow the implementation pattern of the legacy partmodel and is not neccessarily considered good or a best practice.
*/
export const getMergedGeometricAnalysisData = createSelector(
  getPartFileGeometricAnalysisData,
  (allPartFileGeometricAnalysisData) => {
    const mergedGeometricAnalysisData: MergedGeometricAnalysisData = {
      features: [],
      feedbackItems: [],
    };

    Object.keys(allPartFileGeometricAnalysisData).forEach((partFileUuid) => {
      const partFileGeometricAnalysisData =
        allPartFileGeometricAnalysisData?.[partFileUuid] ?? null;
      if (
        partFileGeometricAnalysisData &&
        partFileGeometricAnalysisData.geometricAnalysisData &&
        !partFileGeometricAnalysisData?.isLoading
      ) {
        const { features, feedbackItems } =
          partFileGeometricAnalysisData.geometricAnalysisData;
        mergedGeometricAnalysisData.features =
          mergedGeometricAnalysisData.features.concat(features);
        mergedGeometricAnalysisData.feedbackItems =
          mergedGeometricAnalysisData.feedbackItems.concat(feedbackItems);
      }
    });

    return mergedGeometricAnalysisData;
  },
);
